import React, { useState, useEffect } from 'react';
import { battlesApi } from '../../api/battles';
import HrnbButton from '../controls/HrnbButton';
import HrnbForm from '../controls/HrnbForm';
import HrnbInput from '../controls/HrnbInput';
import HrnbSelect from '../controls/HrnbSelect';
import HrnbTextArea from '../controls/HrnbTextArea';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbModal from '../controls/HrnbModal';
import HrnbDateTimePicker from '../controls/HrnbDateTimePicker';
import HrnbFileUpload from '../controls/HrnbFileUpload';
import HrnbImage from '../controls/HrnbImage';
import { getFormatIcon, getFormatLabel, getFormatDescription, getFormatFileTypes } from '../../utils/battleFormats';
import '../../css/components/CreateBattleForm.css';

const TYPE_OPTIONS = [
    { value: 'SINGLE', label: 'Индивидуальный' },
    { value: 'TEAM', label: 'Командный' },
];

const FORMAT_OPTIONS = [
    { value: 'AUDIO', label: '🎵 Аудио' },
    { value: 'TEXT', label: '📝 Текст' },
];

const AGE_RESTRICTION_OPTIONS = [
    { value: '0', label: 'Без ограничений (0+)' },
    { value: '13', label: '13+' },
    { value: '16', label: '16+' },
    { value: '18', label: '18+' },
];

const VISIBILITY_OPTIONS = [
    { value: 'PUBLIC', label: 'Публичный' },
    { value: 'PRIVATE', label: 'Частный' },
    { value: 'INVITE_ONLY', label: 'По приглашениям' },
];

const STATUS_OPTIONS = [
    { value: 'PLANNED', label: 'Запланирован' },
    { value: 'ACTIVE', label: 'Активен' },
    { value: 'FINISHED', label: 'Завершён' },
    { value: 'CANCELLED', label: 'Отменён' },
];

export default function CreateBattleForm({ isOpen, onClose, onSuccess, useModal = true }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [internalOpen, setInternalOpen] = useState(false);

    // Синхронизация isOpen из родителя
    useEffect(() => {
        setInternalOpen(isOpen);
    }, [isOpen]);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: 'SINGLE',
        format: 'AUDIO',
        status: 'PLANNED',
        visibility: 'PUBLIC',
        ageRestriction: 0,
        startTime: '',
        endTime: '',
        registrationDeadline: '',
        prizePool: '',
        maxParticipants: '',
        coverImageId: null,
        coverFile: null,
        coverPreviewUrl: null,
        tags: [],
        organizers: [],
        judges: [],
        participants: [],
        regulation: {
            rules: [],
            limitations: [],
            formats: null,
            ageLimits: '0+', // По умолчанию без ограничений
        },
    });

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleCoverChange = (e) => {
        const file = e?.target?.files?.[0];
        if (!file) {
            console.warn('[CreateBattleForm] No file selected');
            return;
        }

        console.log('[CreateBattleForm] File selected:', file.name, file.type, file.size);

        // Проверка размера (5 MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Размер файла не должен превышать 5 МБ');
            return;
        }

        // Проверка типа
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Допустимые форматы: JPEG, PNG, WebP');
            return;
        }

        // Создаём preview
        const previewUrl = URL.createObjectURL(file);

        setFormData((prev) => ({
            ...prev,
            coverFile: file,
            coverPreviewUrl: previewUrl,
        }));
        setError(null);
    };

    const validateForm = () => {
        if (!formData.title || !formData.title.trim()) {
            setError('Название баттла обязательно');
            return false;
        }
        if (!formData.description || !formData.description.trim()) {
            setError('Описание обязательно');
            return false;
        }
        if (!formData.startTime) {
            setError('Дата начала обязательна');
            return false;
        }
        if (!formData.endTime) {
            setError('Дата окончания обязательна');
            return false;
        }
        if (formData.startTime && formData.endTime && new Date(formData.startTime) >= new Date(formData.endTime)) {
            setError('Дата начала должна быть раньше даты окончания');
            return false;
        }
        if (formData.maxParticipants && isNaN(parseInt(formData.maxParticipants))) {
            setError('Максимальное количество участников должно быть числом');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            // Сначала создаём баттл
            const payload = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                type: formData.type,
                format: formData.format,
                status: formData.status,
                visibility: formData.visibility,
                ageRestriction: parseInt(formData.ageRestriction) || 0, // Новое поле
                startTime: formData.startTime,
                endTime: formData.endTime,
                registrationDeadline: formData.registrationDeadline || null,
                prizePool: formData.prizePool.trim() || null,
                maxParticipants: formData.maxParticipants ? parseInt(formData.maxParticipants) : null,
                coverImageId: null, // Обложку загрузим после создания
                tags: formData.tags || [],
                organizers: formData.organizers || [],
                judges: formData.judges || [],
                participants: formData.participants || [],
                regulation: (formData.regulation.rules.length > 0 || formData.regulation.formats || formData.regulation.ageLimits) ? {
                    rules: formData.regulation.rules,
                    limitations: formData.regulation.limitations,
                    formats: formData.regulation.formats || null,
                    ageLimits: formData.regulation.ageLimits || '0+',
                } : null,
            };

            const result = await battlesApi.create(payload);

            // Если есть файл обложки, загружаем его
            let finalResult = result;
            if (formData.coverFile && result.id) {
                try {
                    console.log('[CreateBattleForm] Uploading cover for battle', result.id, 'File:', formData.coverFile.name);
                    const coverResponse = await battlesApi.uploadCover(result.id, formData.coverFile);
                    console.log('[CreateBattleForm] Cover uploaded:', coverResponse);
                    finalResult = { ...result, coverImageId: coverResponse.coverImageId };
                } catch (coverErr) {
                    console.error('[CreateBattleForm] Failed to upload cover:', coverErr.message);
                    console.error('[CreateBattleForm] Error response:', coverErr.response?.data);
                    // Прерываем процесс и показываем ошибку, если загрузка не удалась
                    throw new Error(coverErr.response?.data?.message || coverErr.message || 'Не удалось загрузить обложку');
                }
            }

            setSuccess(true);
            setTimeout(() => {
                if (onSuccess) {
                    onSuccess(finalResult);
                }
                handleClose();
            }, 1500);
        } catch (err) {
            console.error('[CreateBattleForm] Failed to create battle:', err.message);
            setError(err.response?.data?.message || 'Не удалось создать баттл');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Очищаем URL превью
        if (formData.coverPreviewUrl) {
            URL.revokeObjectURL(formData.coverPreviewUrl);
        }

        setFormData({
            title: '',
            description: '',
            type: 'SINGLE',
            format: 'AUDIO',
            status: 'PLANNED',
            visibility: 'PUBLIC',
            ageRestriction: 0,
            startTime: '',
            endTime: '',
            registrationDeadline: '',
            prizePool: '',
            maxParticipants: '',
            coverImageId: null,
            coverFile: null,
            coverPreviewUrl: null,
            tags: [],
            organizers: [],
            judges: [],
            participants: [],
            regulation: {
                rules: [],
                limitations: [],
                formats: null,
                ageLimits: '0+',
            },
        });
        setError(null);
        setSuccess(false);
        setInternalOpen(false);
        if (onClose) {
            onClose();
        }
    };

    // Для режима без модального окна всегда показываем форму
    if (!useModal && !isOpen) return null;
    if (useModal && !internalOpen) return null;

    const formContent = (
        <div className={`create-battle-form ${useModal ? 'create-battle-form--modal' : ''}`}>
            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {success && (
                <HrnbAlert variant="success" onClose={() => setSuccess(false)}>
                    Баттл успешно создан!
                </HrnbAlert>
            )}

            <HrnbForm onSubmit={handleSubmit}>
                <div className="form-row">
                    <HrnbInput
                        label="Название *"
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleChange('title', typeof e === 'string' ? e : e.target.value)}
                        placeholder="Введите название баттла"
                        disabled={loading}
                        required
                    />
                </div>

                <div className="form-row">
                    <HrnbTextArea
                        label="Описание *"
                        value={formData.description}
                        onChange={(e) => handleChange('description', typeof e === 'string' ? e : e.target.value)}
                        placeholder="Опишите суть баттла"
                        rows={4}
                        disabled={loading}
                        required
                    />
                </div>

                <div className="form-row form-row-split">
                    <HrnbSelect
                        value={formData.type}
                        onChange={(value) => handleChange('type', value)}
                        options={TYPE_OPTIONS}
                        label="Тип *"
                        placeholder="Выберите тип"
                        disabled={loading}
                    />

                    <HrnbSelect
                        value={formData.format}
                        onChange={(value) => handleChange('format', value)}
                        options={FORMAT_OPTIONS}
                        label="Формат *"
                        placeholder="Выберите формат"
                        disabled={loading}
                    />

                    <HrnbSelect
                        value={formData.status}
                        onChange={(value) => handleChange('status', value)}
                        options={STATUS_OPTIONS}
                        label="Статус *"
                        placeholder="Выберите статус"
                        disabled={loading}
                    />

                    <HrnbSelect
                        value={formData.visibility}
                        onChange={(value) => handleChange('visibility', value)}
                        options={VISIBILITY_OPTIONS}
                        label="Видимость *"
                        placeholder="Выберите видимость"
                        disabled={loading}
                    />
                </div>

                {/* Информация о выбранном формате */}
                <div className="form-row">
                    <div className={`format-info-block format-info-block--${formData.format.toLowerCase()}`}>
                        <div className="format-info-block__header">
                            <span className="format-info-block__icon">{getFormatIcon(formData.format)}</span>
                            <strong>{getFormatLabel(formData.format)}</strong>
                        </div>
                        <p className="format-info-block__description">
                            {getFormatDescription(formData.format)}
                        </p>
                        <div className="format-info-block__details">
                            <span>📁 {getFormatFileTypes(formData.format).hint}</span>
                            <span>💾 Макс. размер: {Math.round(getFormatFileTypes(formData.format).maxFileSize / 1024 / 1024)} МБ</span>
                        </div>
                    </div>
                </div>


                <div className="form-row form-row-split">
                    <HrnbDateTimePicker
                        label="Дата начала *"
                        value={formData.startTime}
                        onChange={(value) => handleChange('startTime', value)}
                        disabled={loading}
                        placeholder="Выберите дату и время"
                        required
                    />

                    <HrnbDateTimePicker
                        label="Дата окончания *"
                        value={formData.endTime}
                        onChange={(value) => handleChange('endTime', value)}
                        disabled={loading}
                        placeholder="Выберите дату и время"
                        required
                    />
                </div>

                <div className="form-row form-row-split">
                    <HrnbDateTimePicker
                        label="Регистрация до"
                        value={formData.registrationDeadline}
                        onChange={(value) => handleChange('registrationDeadline', value)}
                        disabled={loading}
                        placeholder="Выберите дату и время"
                    />

                    <HrnbInput
                        label="Макс. участников"
                        type="number"
                        value={formData.maxParticipants}
                        onChange={(e) => handleChange('maxParticipants', typeof e === 'string' ? e : e.target.value)}
                        placeholder="Неограниченно"
                        min="1"
                        disabled={loading}
                    />
                </div>

                <div className="form-row">
                    <HrnbInput
                        label="Призовой фонд"
                        type="text"
                        value={formData.prizePool}
                        onChange={(e) => handleChange('prizePool', typeof e === 'string' ? e : e.target.value)}
                        placeholder="Например: 10000 RUB"
                        disabled={loading}
                    />
                </div>

                <div className="form-row">
                    <label className="hrnb-input-label">Обложка баттла</label>
                    <HrnbFileUpload
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleCoverChange}
                        disabled={loading}
                        hint="Макс. размер: 5 МБ. Форматы: JPEG, PNG, WebP"
                    />
                    {formData.coverPreviewUrl && (
                        <div className="battle-cover-preview" style={{ marginTop: 12 }}>
                            <HrnbImage
                                src={formData.coverPreviewUrl}
                                alt="Предпросмотр обложки"
                                style={{
                                    maxWidth: 300,
                                    maxHeight: 300,
                                    borderRadius: 8,
                                    objectFit: 'cover',
                                }}
                            />
                            <HrnbButton
                                variant="outline-danger"
                                size="sm"
                                onClick={() => setFormData(prev => ({
                                    ...prev,
                                    coverFile: null,
                                    coverPreviewUrl: null,
                                }))}
                                style={{ marginTop: 8 }}
                            >
                                Удалить обложку
                            </HrnbButton>
                        </div>
                    )}
                </div>

                <div className="form-row">
                    <HrnbSelect
                        value={String(formData.ageRestriction)}
                        onChange={(value) => handleChange('ageRestriction', parseInt(value))}
                        options={AGE_RESTRICTION_OPTIONS}
                        label="Возрастное ограничение (ageRestriction)"
                        placeholder="Выберите возрастное ограничение"
                        disabled={loading}
                    />
                </div>

                <div className="form-row">
                    <HrnbInput
                        label="Возрастные ограничения регламента (ageLimits)"
                        type="text"
                        value={formData.regulation.ageLimits || ''}
                        onChange={(e) => handleChange('regulation', {
                            ...formData.regulation,
                            ageLimits: typeof e === 'string' ? e : e.target.value || '0+',
                        })}
                        placeholder="Например: 0+, 13+, 16+, 18+"
                        disabled={loading}
                        hint="Возрастные ограничения для регламента баттла"
                    />
                </div>

                <div className="form-row">
                    <HrnbInput
                        label="Форматы (formats)"
                        type="text"
                        value={formData.regulation.formats || ''}
                        onChange={(e) => handleChange('regulation', {
                            ...formData.regulation,
                            formats: typeof e === 'string' ? e : e.target.value || null,
                        })}
                        placeholder="Например: 1v1, 2v2"
                        disabled={loading}
                    />
                </div>

                <div className="form-actions">
                    <HrnbButton variant="outline" onClick={handleClose} disabled={loading}>
                        Отмена
                    </HrnbButton>
                    <HrnbButton variant="primary" type="submit" disabled={loading}>
                        {loading ? <HrnbLoader size="sm" /> : 'Создать баттл'}
                    </HrnbButton>
                </div>
            </HrnbForm>
        </div>
    );

    // Если useModal=false, рендерим форму напрямую без HrnbModal
    if (!useModal) {
        return formContent;
    }

    // Режим модального окна (по умолчанию)
    return (
        <HrnbModal open={internalOpen} onClose={handleClose} title="Создание нового баттла" size="large">
            {formContent}
        </HrnbModal>
    );
}
